/*  
This component filters based on account, cluster, namespace based on which the image and its version are shown. This component is called
from Home.js file and rendered as part of service inventory page.

This component has dropbox (for selection of aws account), checkbox (for selection of cluster and namespace), search input box( filters 
the table records based on the given input).
*/

import React, { useState, useEffect, useRef } from 'react';
import './Dropbox.css';

const Dropdown = ({ 
  onSearch, 
  onSelectEnvironment, 
  onSelectCluster, 
  onSelectNamespace, 
  environments, 
  selectedCluster, 
  environmentData = []
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [clusterSearchTerm, setClusterSearchTerm] = useState('');
  const [namespaceSearchTerm, setNamespaceSearchTerm] = useState('');
  const [namespaces, setNamespaces] = useState([]);
  const [selectedNamespaces, setSelectedNamespaces] = useState(new Set());
  const [selectedClusters, setSelectedClusters] = useState(new Set());
  const [isNamespaceOpen, setIsNamespaceOpen] = useState(false);
  const [isClusterOpen, setIsClusterOpen] = useState(false);
  const [originalData, setOriginalData] = useState([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState('');
  const [showClusterTooltip, setShowClusterTooltip] = useState(false);
  const [showNamespaceTooltip, setShowNamespaceTooltip] = useState(false);
  const [cluster, setcluster] = useState(false);
  const [namespace, setnamespace] = useState(false);

  const clusterDropdownRef = useRef(null);
  const namespaceDropdownRef = useRef(null);
  const clusterSearchRef = useRef(null);
  const namespaceSearchRef = useRef(null);

  useEffect(() => {
    if (selectedClusters.size > 0 && Array.isArray(environmentData)) {
      const clusterData = environmentData.filter(item => selectedClusters.has(item.cluster));
      const uniqueNamespaces = [...new Set(clusterData.map(item => item.namespace))];
      setNamespaces(uniqueNamespaces);
      setOriginalData(clusterData);
    }
  }, [environmentData, selectedClusters]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (clusterDropdownRef.current && !clusterDropdownRef.current.contains(event.target)) {
        setIsClusterOpen(false);
      }
      if (namespaceDropdownRef.current && !namespaceDropdownRef.current.contains(event.target)) {
        setIsNamespaceOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    onSearch(value);
  };

  const handleEnvironmentChange = (event) => {
    const environment = event.target.value;
    setSelectedEnvironment(environment);
    onSelectEnvironment(environment);
    setNamespaces([]);
    setOriginalData([]);
    setSelectedClusters(new Set());
    onSelectCluster([]);
    onSelectNamespace([]);
    setSearchTerm('');
    onSearch('');
    setSelectedNamespaces(new Set());
  };

  const handleClusterCheckboxChange = (cluster) => {
    const newSelectedClusters = new Set(selectedClusters);
    
    if (cluster === 'Select All') {
      if (newSelectedClusters.has('Select All')) {
        newSelectedClusters.clear();
        setcluster(false);
      } else {
        newSelectedClusters.clear();
        newSelectedClusters.add('Select All');
        clusters.forEach(c => newSelectedClusters.add(c));
        setcluster(true);
      }
    } else {
      if (newSelectedClusters.has(cluster)) {
        newSelectedClusters.delete(cluster);
        newSelectedClusters.delete('Select All');
        setcluster(false);
      } else {
        newSelectedClusters.add(cluster);
        if (newSelectedClusters.size === clusters.length) {
          newSelectedClusters.add('Select All');
          setcluster(true);
        }
      }
    }
    
    setSelectedClusters(newSelectedClusters);
    onSelectCluster(Array.from(newSelectedClusters));
  };

  const handleSelectAllClusters = (checked) => {
    const newSelectedClusters = new Set();
    if (checked) {
      newSelectedClusters.add('Select All');
      clusters.forEach(cluster => newSelectedClusters.add(cluster));
    }
    setcluster(checked);
    setSelectedClusters(newSelectedClusters);
    onSelectCluster(Array.from(newSelectedClusters));
  };

  const handleSelectAll = (checked) => {
    const newSelectedNamespaces = new Set();
    if (checked) {
      newSelectedNamespaces.add('Select All');
      namespaces.forEach(namespace => newSelectedNamespaces.add(namespace));
    }
    setnamespace(checked);
    setSelectedNamespaces(newSelectedNamespaces);
    onSelectNamespace(Array.from(newSelectedNamespaces));
  };

  const handleCheckboxChange = (namespace) => {
    const newSelectedNamespaces = new Set(selectedNamespaces);
    
    if (namespace === 'Select All') {
      if (newSelectedNamespaces.has('Select All')) {
        newSelectedNamespaces.clear();
        setnamespace(false);
      } else {
        newSelectedNamespaces.clear();
        newSelectedNamespaces.add('Select All');
        namespaces.forEach(ns => newSelectedNamespaces.add(ns));
        setnamespace(true);
      }
    } else {
      if (newSelectedNamespaces.has(namespace)) {
        newSelectedNamespaces.delete(namespace);
        newSelectedNamespaces.delete('Select All');
        setnamespace(false);
      } else {
        newSelectedNamespaces.add(namespace);
        if (newSelectedNamespaces.size === namespaces.length) {
          newSelectedNamespaces.add('Select All');
          setnamespace(true);
        }
      }
    }
    
    setSelectedNamespaces(newSelectedNamespaces);
    onSelectNamespace(Array.from(newSelectedNamespaces));
  };

  const getClusterDisplayText = () => {
    if (selectedClusters.size === 0) return 'Select Cluster';
    if (selectedClusters.has('Select All')) return 'All Clusters';
    const clusterArray = Array.from(selectedClusters);
    if (clusterArray.length === 1) return clusterArray[0];
    return `${clusterArray.length} selected`;
  };

  const getNamespaceDisplayText = () => {
    if (selectedNamespaces.size === 0) return 'Select Namespace';
    if (selectedNamespaces.has('Select All')) return 'All Namespaces';
    const namespaceArray = Array.from(selectedNamespaces);
    if (namespaceArray.length === 1) return namespaceArray[0];
    return `${namespaceArray.length} selected`;
  };

  const TooltipContent = ({ items }) => (
    <div className="tooltip-content">
      {Array.from(items).filter(item => item !== 'Select All').join(', ')}
    </div>
  );
  
  const toggleNamespaceDropdown = () => {
    if (selectedClusters.size > 0) {
      setIsNamespaceOpen(!isNamespaceOpen);
    }
  };

  const toggleClusterDropdown = () => {
    if (selectedEnvironment) {
      setIsClusterOpen(!isClusterOpen);
    }
  };

  const clusters = Array.isArray(environmentData)
    ? [...new Set(environmentData.map(item => item.cluster))]
    : [];

  console.log(originalData);
  console.log(cluster);
  console.log(namespace);

  const filteredClusters = clusters.filter(cluster =>
    cluster.toLowerCase().includes(clusterSearchTerm.toLowerCase())
  );

  // Filter namespaces based on search term
  const filteredNamespaces = namespaces.filter(namespace =>
    namespace.toLowerCase().includes(namespaceSearchTerm.toLowerCase())
  );

  // Prevent dropdown from closing when clicking search input
  const handleSearchClick = (e, searchRef) => {
    e.stopPropagation();
    searchRef.current.focus();
  };

  return (
    <div className="dropdown-container">
      <div className="aws-account-container">
        <label htmlFor="env">AWS Account:</label>
        <select id="env" onChange={handleEnvironmentChange}>
          <option value="">Select Account</option>
          {environments.map((env) => (
            <option key={env} value={env}>
              {env}
            </option>
          ))}
        </select>
      </div>

      <div className="dropdown-item" ref={clusterDropdownRef}>
        <label htmlFor="cluster">Cluster:</label>
        <div className="namespace-dropdown">
          <div 
            className={`namespace-select-header ${!selectedEnvironment ? 'disabled' : ''}`}
            onClick={toggleClusterDropdown}
            onMouseEnter={() => setShowClusterTooltip(true)}
            onMouseLeave={() => setShowClusterTooltip(false)}
          >
            {getClusterDisplayText()}
            {showClusterTooltip && selectedClusters.size > 0 && (
              <TooltipContent items={selectedClusters} />
            )}
          </div>
          {isClusterOpen && (
            <div className="namespace-options">
              <div className="search-container" onClick={(e) => handleSearchClick(e, clusterSearchRef)}>
                
                <input
                  ref={clusterSearchRef}
                  type="text"
                  placeholder="Search clusters..."
                  value={clusterSearchTerm}
                  onChange={(e) => setClusterSearchTerm(e.target.value)}
                  className="dropdown-search"
                />
              </div>
              <div className="namespace-option select-all">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedClusters.has('Select All')}
                    onChange={(e) => handleSelectAllClusters(e.target.checked)}
                  />
                  <span className="checkbox-text">Select All</span>
                </label>
              </div>
              {filteredClusters.map((cluster, index) => (
                <div key={index} className="namespace-option">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={selectedClusters.has(cluster)}
                      onChange={() => handleClusterCheckboxChange(cluster)}
                    />
                    <span className="checkbox-text">{cluster}</span>
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="dropdown-item" ref={namespaceDropdownRef}>
        <label htmlFor="namespace">Namespace:</label>
        <div className="namespace-dropdown">
          <div 
            className={`namespace-select-header ${!selectedClusters.size ? 'disabled' : ''}`}
            onClick={toggleNamespaceDropdown}
            onMouseEnter={() => setShowNamespaceTooltip(true)}
            onMouseLeave={() => setShowNamespaceTooltip(false)}
          >
            {getNamespaceDisplayText()}
            {showNamespaceTooltip && selectedNamespaces.size > 0 && (
              <TooltipContent items={selectedNamespaces} />
            )}
          </div>
          {isNamespaceOpen && (
            <div className="namespace-options">
              <div className="search-container" onClick={(e) => handleSearchClick(e, namespaceSearchRef)}>
                <input
                  ref={namespaceSearchRef}
                  type="text"
                  placeholder="Search namespaces..."
                  value={namespaceSearchTerm}
                  onChange={(e) => setNamespaceSearchTerm(e.target.value)}
                  className="dropdown-search"
                />
              </div>
              <div className="namespace-option select-all">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedNamespaces.has('Select All')}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                  <span className="checkbox-text">Select All</span>
                </label>
              </div>
              {filteredNamespaces.map((namespace, index) => (
                <div key={index} className="namespace-option">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={selectedNamespaces.has(namespace)}
                      onChange={() => handleCheckboxChange(namespace)}
                    />
                    <span className="checkbox-text">{namespace}</span>
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="search-bar">
        <label htmlFor="search"></label>
        <input
          type="text"
          id="search"
          placeholder="Search..."
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </div>
    </div>
  );
};

export default Dropdown;